import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { customersAPI } from "../services/api";

const AddCustomerDialog = ({ open, onClose, onCustomerAdded }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "",
    birthdate: "",
    contact: "",
    address: "",
    note: "",
  });

  const genderChoices = [
    { value: "male", label: "ذكر" },
    { value: "female", label: "أنثى" },
  ];

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "اسم العميل مطلوب";
    }

    if (!formData.contact.trim()) {
      newErrors.contact = "معلومات التواصل مطلوبة";
    }

    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "البريد الإلكتروني غير صحيح";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Prepare data for API
      const customerData = {
        name: formData.name.trim(),
        contact: formData.contact.trim(),
        email: formData.email.trim() || null,
        gender: formData.gender || null,
        birthdate: formData.birthdate || null,
        address: formData.address.trim() || null,
        note: formData.note.trim() || null,
      };

      const newCustomer = await customersAPI.createCustomer(customerData);

      // Reset form
      setFormData({
        name: "",
        email: "",
        gender: "",
        birthdate: "",
        contact: "",
        address: "",
        note: "",
      });

      // Notify parent component
      if (onCustomerAdded) {
        onCustomerAdded(newCustomer);
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error("Error creating customer:", error);

      // تحسين معالجة الأخطاء
      let errorMessage = "حدث خطأ في إنشاء العميل";

      if (error.message) {
        // معالجة أخطاء مختلفة
        if (error.message.includes("400")) {
          errorMessage =
            "البيانات المدخلة غير صحيحة. يرجى التحقق من جميع الحقول المطلوبة";
        } else if (error.message.includes("401")) {
          errorMessage = "انتهت جلسة المستخدم. يرجى تسجيل الدخول مرة أخرى";
        } else if (error.message.includes("403")) {
          errorMessage = "ليس لديك صلاحية لإضافة عملاء جدد";
        } else if (error.message.includes("500")) {
          errorMessage = "خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً";
        } else if (
          error.message.includes("Network Error") ||
          error.message.includes("Failed to fetch")
        ) {
          errorMessage = "خطأ في الاتصال بالخادم. تأكد من اتصال الإنترنت";
        } else {
          errorMessage = error.message;
        }
      }

      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form and errors when closing
      setFormData({
        name: "",
        email: "",
        gender: "",
        birthdate: "",
        contact: "",
        address: "",
        note: "",
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? "rtl" : "ltr"}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon color="primary" />
          <Typography variant="h6" component="div">
            إضافة عميل جديد
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.general}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {/* Name and Contact - Required fields */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              fullWidth
              label="اسم العميل *"
              value={formData.name}
              onChange={handleInputChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              required
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />

            <TextField
              fullWidth
              label="معلومات التواصل *"
              value={formData.contact}
              onChange={handleInputChange("contact")}
              error={!!errors.contact}
              helperText={errors.contact || "رقم هاتف أو إيميل أو غيره"}
              required
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />
          </Box>

          {/* Email and Gender */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              fullWidth
              label="البريد الإلكتروني"
              type="email"
              value={formData.email}
              onChange={handleInputChange("email")}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />

            <FormControl
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            >
              <InputLabel>الجنس</InputLabel>
              <Select
                value={formData.gender}
                onChange={handleInputChange("gender")}
                label="الجنس"
              >
                <MenuItem value="">
                  <em>اختر الجنس</em>
                </MenuItem>
                {genderChoices.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Birthdate */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="تاريخ الميلاد"
              type="date"
              value={formData.birthdate}
              onChange={handleInputChange("birthdate")}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />
          </Box>

          {/* Address */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="العنوان"
              multiline
              rows={2}
              value={formData.address}
              onChange={handleInputChange("address")}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />
          </Box>

          {/* Note */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="ملاحظات"
              multiline
              rows={2}
              value={formData.note}
              onChange={handleInputChange("note")}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          size="large"
          sx={{
            minWidth: "120px",
            height: "48px",
            fontSize: "16px",
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          size="large"
          startIcon={
            isSubmitting ? <CircularProgress size={20} /> : <AddIcon />
          }
          sx={{
            minWidth: "140px",
            height: "48px",
            fontSize: "16px",
          }}
        >
          {isSubmitting ? "جاري الإضافة..." : "إضافة العميل"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerDialog;
